/**
 * DocumentsStatsSheet — Expandable bottom sheet for Documents page
 *
 * Collapsed: Shows summary stats with expand arrow
 * Expanded: Shows detailed stats in two groups:
 *   - File Stats: Total Files, Total Size, File Types chart, Document Types chart
 *   - Workflow Status: Indexing Status, Processing Status
 */

import {
  ChevronDown,
  ChevronUp,
  Clock,
  Database,
  FileCheck,
  FileClock,
  HardDrive,
} from 'lucide-react'
import { useState } from 'react'
import {
  Legend,
  PolarAngleAxis,
  PolarGrid,
  Radar,
  RadarChart,
  RadialBar,
  RadialBarChart,
  Tooltip,
} from 'recharts'
import { ChartContainer } from '@/components/ui/chart'
import type { LakeStats } from '@/services/api/lake'
import { formatDocumentType } from '@/utils/document-labels'

// ── Palette ──────────────────────────────────────────────────────────────────

const COLORS = [
  'oklch(0.555 0.163 48.998)', // primary orange
  'oklch(0.769 0.188 70.08)', // amber
  'oklch(0.627 0.265 303.9)', // purple
  'oklch(0.645 0.246 16.439)', // red
  'oklch(0.7 0.2 220)', // blue
  'oklch(0.879 0.169 91.605)', // yellow-green
]

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${(bytes / k ** i).toFixed(2)} ${sizes[i]}`
}

function _formatLabel(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

// ── Tooltip helpers ───────────────────────────────────────────────────────────

function FileTypeTooltip({
  active,
  payload,
}: {
  active?: boolean
  payload?: { value?: number; payload?: { name?: string; fill?: string } }[]
}) {
  if (!active || !payload?.length) return null
  const d = payload[0]
  return (
    <div className="rounded border border-border bg-popover px-2.5 py-1.5 text-xs shadow-md">
      <span style={{ color: d.payload?.fill }} className="font-medium">
        {d.payload?.name}
      </span>
      <span className="ml-2 text-muted-foreground">{d.value} files</span>
    </div>
  )
}

function DocTypeTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean
  payload?: { value?: number }[]
  label?: string
}) {
  if (!active || !payload?.length) return null
  return (
    <div className="rounded border border-border bg-popover px-2.5 py-1.5 text-xs shadow-md">
      <p className="font-medium text-foreground mb-0.5">{label}</p>
      <p className="text-muted-foreground">{payload[0]?.value} documents</p>
    </div>
  )
}

// ── Component ─────────────────────────────────────────────────────────────────

interface DocumentsStatsSheetProps {
  stats: LakeStats | null
}

export function DocumentsStatsSheet({ stats }: DocumentsStatsSheetProps) {
  const [expanded, setExpanded] = useState(false)

  if (!stats) return null

  // Prepare file type data (top 6 by count)
  const fileTypeData = Object.entries(stats.by_file_type)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 6)
    .map(([type, count], i) => ({
      name: type.toUpperCase(),
      count,
      fill: COLORS[i % COLORS.length],
    }))

  // Prepare file size breakdown (top 6 by size, descending)
  const fileSizeEntries = Object.entries(stats.by_file_type_size)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 6)

  // Prepare document type data (top 8 by count)
  const docTypeData = Object.entries(stats.by_document_type)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 8)
    .map(([type, count]) => ({
      subject: formatDocumentType(type),
      count,
    }))

  const completionPct =
    stats.total_documents > 0 ? Math.round((stats.completed / stats.total_documents) * 100) : 0

  return (
    <div className="flex-shrink-0">
      {/* Collapsed bar */}
      <div className="flex items-center justify-between gap-3 px-4 py-2 border-t border-border text-xs text-muted-foreground bg-background md:gap-0 md:px-6">
        {/* Phone: the bar keeps the three headline counts; processing and
            pending are in the expanded panel. */}
        <div className="flex items-center gap-3 whitespace-nowrap md:gap-4 md:whitespace-normal">
          <span>{stats.total_documents} items</span>
          <span>{stats.completed} indexed</span>
          <span className="max-md:hidden">{stats.processing} processing</span>
          <span className="max-md:hidden">{stats.pending_indexing} pending</span>
          <span>{stats.failed} failed</span>
        </div>
        <div className="flex items-center gap-2 whitespace-nowrap md:whitespace-normal">
          <span>{formatBytes(stats.total_size_bytes)} used</span>
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-2.5 hover:bg-muted rounded transition-colors md:p-1"
            aria-label={expanded ? 'Collapse stats' : 'Expand stats'}
          >
            {expanded ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronUp className="h-3.5 w-3.5" />
            )}
          </button>
        </div>
      </div>

      {/* Expanded panel */}
      {expanded && (
        <div className="border-t border-border max-md:max-h-[60dvh] max-md:overflow-y-auto">
          <div className="p-4 space-y-4 bg-background">
            {/* FILE STATS */}
            <div className="space-y-3">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                File Stats
              </h3>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {/* Total Files */}
                <div className="rounded-lg border border-border p-4">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Database className="h-3.5 w-3.5 text-blue-500" />
                    <span className="text-xs font-medium text-muted-foreground">Total Files</span>
                  </div>
                  <div className="text-xl font-bold tabular-nums md:text-2xl">
                    {stats.total_documents.toLocaleString()}
                  </div>
                </div>

                {/* Total Size */}
                <div className="rounded-lg border border-border p-4">
                  <div className="flex items-center gap-1.5 mb-2">
                    <HardDrive className="h-3.5 w-3.5 text-purple-500" />
                    <span className="text-xs font-medium text-muted-foreground">Total Size</span>
                  </div>
                  <div className="text-xl font-bold tabular-nums mb-2 md:text-2xl">
                    {formatBytes(stats.total_size_bytes)}
                  </div>
                  {fileSizeEntries.length > 0 && (
                    <div className="space-y-1">
                      {fileSizeEntries.map(([type, bytes], i) => (
                        <div key={type} className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <span
                              className="h-1.5 w-1.5 rounded-full shrink-0"
                              style={{ background: COLORS[i % COLORS.length] }}
                            />
                            <span className="text-[10px] text-muted-foreground uppercase font-mono">
                              {type}
                            </span>
                          </div>
                          <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">
                            {formatBytes(bytes)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* File Types — Radial Bar Chart */}
                <div className="rounded-lg border border-border p-4 max-md:col-span-2">
                  <span className="text-xs font-medium text-muted-foreground">File Types</span>
                  <div className="mt-2">
                    {fileTypeData.length > 0 ? (
                      <ChartContainer height={150}>
                        <RadialBarChart
                          cx="50%"
                          cy="50%"
                          innerRadius="20%"
                          outerRadius="85%"
                          startAngle={90}
                          endAngle={-270}
                          data={fileTypeData}
                        >
                          <RadialBar
                            dataKey="count"
                            cornerRadius={3}
                            background={{ fill: 'hsl(var(--muted))' }}
                          />
                          <Tooltip content={<FileTypeTooltip />} />
                          <Legend
                            iconType="circle"
                            iconSize={7}
                            wrapperStyle={{ fontSize: '10px', paddingTop: '4px' }}
                            formatter={(value: string) => (
                              <span className="text-muted-foreground">{value}</span>
                            )}
                          />
                        </RadialBarChart>
                      </ChartContainer>
                    ) : (
                      <p className="text-xs text-muted-foreground italic py-4">No files yet</p>
                    )}
                  </div>
                </div>

                {/* Document Types — Radar Chart */}
                <div className="rounded-lg border border-border p-4 max-md:col-span-2">
                  <span className="text-xs font-medium text-muted-foreground">Document Types</span>
                  <div className="mt-2">
                    {docTypeData.length > 0 ? (
                      <ChartContainer height={150}>
                        <RadarChart
                          data={docTypeData}
                          margin={{ top: 10, right: 25, bottom: 10, left: 25 }}
                        >
                          <PolarGrid stroke="hsl(var(--border))" />
                          <PolarAngleAxis
                            dataKey="subject"
                            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 9 }}
                            tickLine={false}
                          />
                          <Radar
                            name="Count"
                            dataKey="count"
                            stroke={COLORS[0]}
                            fill={COLORS[0]}
                            fillOpacity={0.2}
                            strokeWidth={1.5}
                          />
                          <Tooltip content={<DocTypeTooltip />} />
                        </RadarChart>
                      </ChartContainer>
                    ) : (
                      <p className="text-xs text-muted-foreground italic py-4">
                        No AI analysis yet
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* WORKFLOW STATUS */}
            <div className="space-y-3">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Workflow Status
              </h3>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {/* Indexing Status */}
                <div className="rounded-lg border border-border p-4 space-y-3">
                  <span className="text-xs font-medium text-muted-foreground">Indexing Status</span>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileCheck className="h-3.5 w-3.5 text-success" />
                      <span className="text-xs text-muted-foreground">Indexed</span>
                    </div>
                    <span className="text-sm font-semibold tabular-nums">
                      {stats.completed.toLocaleString()}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileClock className="h-3.5 w-3.5 text-warning" />
                      <span className="text-xs text-muted-foreground">Pending</span>
                    </div>
                    <span className="text-sm font-semibold tabular-nums">
                      {stats.pending_indexing.toLocaleString()}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Clock className="h-3.5 w-3.5 text-warning" />
                      <span className="text-xs text-muted-foreground">Processing</span>
                    </div>
                    <span className="text-sm font-semibold tabular-nums">
                      {stats.processing.toLocaleString()}
                    </span>
                  </div>
                </div>

                {/* Processing Status */}
                <div className="rounded-lg border border-border p-4 space-y-3">
                  <span className="text-xs font-medium text-muted-foreground">
                    Processing Status
                  </span>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="h-2.5 w-2.5 rounded-full bg-success" />
                      <span className="text-xs text-muted-foreground">Completed</span>
                    </div>
                    <span className="text-sm font-semibold tabular-nums">
                      {stats.completed.toLocaleString()}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="h-2.5 w-2.5 rounded-full bg-warning animate-pulse" />
                      <span className="text-xs text-muted-foreground">Processing</span>
                    </div>
                    <span className="text-sm font-semibold tabular-nums">
                      {stats.processing.toLocaleString()}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="h-2.5 w-2.5 rounded-full bg-destructive" />
                      <span className="text-xs text-muted-foreground">Failed</span>
                    </div>
                    <span className="text-sm font-semibold tabular-nums">
                      {stats.failed.toLocaleString()}
                    </span>
                  </div>

                  {/* Progress bar */}
                  <div className="pt-2 border-t border-border">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[10px] text-muted-foreground">Overall completion</span>
                      <span className="text-[10px] font-semibold tabular-nums">
                        {completionPct}%
                      </span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden flex">
                      {stats.total_documents > 0 && (
                        <>
                          <div
                            className="bg-success"
                            style={{
                              width: `${(stats.completed / stats.total_documents) * 100}%`,
                            }}
                          />
                          <div
                            className="bg-warning"
                            style={{
                              width: `${(stats.processing / stats.total_documents) * 100}%`,
                            }}
                          />
                          <div
                            className="bg-destructive"
                            style={{
                              width: `${(stats.failed / stats.total_documents) * 100}%`,
                            }}
                          />
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
